import React, { useEffect, useState } from "react";
import { z } from "zod";
import apiClient from "@/services";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { db } from "@/utils/firebase";
import { doc, setDoc } from "firebase/firestore";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import Spinner from "@/components/Spinner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import WhatsAppButton from "@/components/WhatsAppButton";
import getConstants from "@/scripts/getConstants";

const formSchema = z.object({
  first_name: z
    .string()
    .min(1, "Name is required")
    .max(25, "Name cannot exceed 20 characters"),
  last_name: z
    .string()
    .min(1, "Name is required")
    .max(25, "Name cannot exceed 20 characters"),
  email: z.string().min(1, "Email is required").email("Invalid email format"),
  password: z
    .string()
    .min(8, "Password should be atleast 8 characters")
    .max(15, "Name cannot exceed 15 characters"),
  phone: z
    .string()
    .min(
      10,
      "Phone number must be at least 10 digits, including the country code"
    )
    .max(15, "Phone number cannot exceed 15 digits")
    .regex(
      /^\+[1-9]\d{9,14}$/,
      "Phone number must start with '+' followed by a valid country code and digits"
    ),
  role: z.string().min(1, "Role is required"), // Expecting a single string now
  company: z
    .string()
    .min(1, "Company name is required")
    .max(25, "Company name cannot exceed 25 characters"),
  source: z
    .string()
    .optional(),
  grade: z
    .array(z.string())
    .optional(),
  form: z
    .array(z.string())
    .optional(),
});

function DeleteAlert({ setVisible, email, deleteAccount }) {
  return (
    <AlertDialog defaultOpen={true}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Action</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete the client account {email}?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="secondary" onClick={() => setVisible(false)}>
            Cancel
          </Button>
          <Button
            onClick={deleteAccount}
            className="bg-red-600 hover:bg-red-600"
          >
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function Clients() {
  const [agents, setAgents] = useState();
  const [mode, setMode] = useState("agents");
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [editingUid, setEditingUid] = useState(null);
  
  // Constants
  const [forms, setForms] = useState([]);
  const [grades, setGrades] = useState([]);

  // Delete Account
  const [accountToDeleteUid, setAccountToDeleteUid] = useState("");
  const [accountToDeleteEmail, setAccountToDeleteEmail] = useState("");

  // Filters
  const [filters, setFilters] = useState({
    role: "all",
    search: "",
  });
  const [filteredList, setFilteredList] = useState([]);

  const roleMap = {
    Buyer: ["buyer"],
    Seller: ["seller"],
    "Buyer & Seller": ["buyer", "seller"],
  };

  const editSchema = formSchema.omit({ email: true, password: true, role: true });

  const form = useForm({
    resolver: zodResolver(mode === "edit" ? editSchema : formSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      password: "",
      company: "",
      role: "", // Default to an empty string
      source: "",
      grade: [],
      form: []
    },
  });

  // Define columns for the table
  const columns = [
    {
      accessorKey: "sno",
      header: "Sno",
      cell: (info) => info.row.index + 1, // Automatically generate Sno
    },
    {
      accessorKey: "name",
      header: "Name",
      cell: (info) =>
        `${info.row.original.first_name} ${info.row.original.last_name}`,
    },
    {
      accessorKey: "company",
      header: "Company",
    },
    {
      accessorKey: "email",
      header: "Email",
    },
    {
      accessorKey: "phone",
      header: "Phone",
    },
    {
      accessorKey: "source",
      header: "Account Source",
    },
    {
      accessorKey: "agent",
      header: "Agent Name",
    },
    {
      accessorKey: "roles",
      header: "Roles",
      cell: (info) => formatRoles(info.getValue()),
    },
    {
      accessorKey: "actions",
      header: "Actions",
      cell: (info) => (
        <div className="flex gap-4">
          <WhatsAppButton phone={info.row.original.phone} />

          <Button
            className="px-6 rounded-md"
            onClick={() => handleEditAccount(info.row.original)}
          >
            Edit Account
          </Button>

          <Button
            className="px-6 rounded-md bg-red-600 hover:bg-red-600"
            onClick={() =>
              handleDelete(info.row.original.email, info.row.original.uid)
            }
            >
            Delete Account
          </Button>
        </div>
      ),
    },
  ];

  // Initialize the table
  const table = useReactTable({
    columns,
    data: filteredList,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10, // Default page size
      },
    },
  });

  // Create Agent Account
  const onSubmit = async (values) => {
    try {
      setLoading(true);
      const { status } = await apiClient.post("/createUser", {
        ...values,
        roles: roleMap[values.role],
      });
      if (status === 201) {
        // Reset form data
        form.reset();
        alert("Client account created successfully");
        window.location.reload();
      }
    } catch (err) {
      alert("Error creating client account");
      console.log("Error creating client account", err);
    } finally {
      setLoading(false);
    }
  };

  // Format Roles
  function formatRoles(roles) {
    if (!Array.isArray(roles)) return "";

    return roles
      .map((role) => {
        if (!role) return "";

        // Split by underscore
        const words = role.split("_");

        // Capitalize first letter of each word and join with space
        return words
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      })
      .filter(Boolean) // Remove any empty strings
      .join(", "); // Join with comma and space
  }

  // Format Roles
  function formatRole(role) {
    if (!role) return "";

    // Split by underscore
    const words = role.split("_");

    // Capitalize first letter of each word and join with space
    return words
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  // Update filters when inputs change
  const handleRoleChange = (value) => {
    setFilters((prev) => ({ ...prev, role: value }));
  };

  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
  };

  // Deletion Alert
  const handleDelete = (email, uid) => {
    setVisible(true);
    setAccountToDeleteUid(uid);
    setAccountToDeleteEmail(email);
  };

  // Delete Account
  const deleteAccount = async () => {
    if (!accountToDeleteUid) {
      alert("Please select an account to delete!");
      return;
    }

    try {
      setLoading(true);
      const { status } = await apiClient.post("/deleteAgent", {
        accountToDeleteUid,
      });
      if (status === 200) {
        alert("Account deleted sucessfully");
        window.location.reload();
      }
    } catch (err) {
      alert("Failed to delete the account");
      console.log("Error deleting client account", err);
    } finally {
      setLoading(false);
    }
  };

  // Edit Account
  const handleEditAccount = (account) => {
    setMode("edit");
    setEditingUid(account.uid);
    form.setValue("first_name", account.first_name || "");
    form.setValue("last_name", account.last_name || "");
    form.setValue("phone", account.phone || "");
    form.setValue("company", account.company || "");
    form.setValue("grade", account?.preferences?.grade || []);
    form.setValue("form", account?.preferences?.form || []);
  };

  // Update Details
  const onUpdate = async (values) => {
    try {
      setLoading(true);
      const userDocRef = doc(db, "users", editingUid);
      
      await setDoc(userDocRef, {
        first_name: values.first_name,
        last_name: values.last_name,
        phone: values.phone,
        company: values.company,
        preferences: {
          grade: values.grade,
          form: values.form,
        },
      }, { merge: true });
      
      form.reset();
      setMode("agents")
      alert("Client account updated successfully");
    } catch (err) {
      alert("Error saving client account");
      console.error("Submit error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters whenever filters state changes
  const filterProducts = () => {
    const filtered = agents.filter((agent) => {
      // Filter by role
      const roleMatch =
        filters.role === "all" ||
        (Array.isArray(agent.roles) && agent.roles.includes(filters.role));

      // Filter by search term (name, email, phone)
      const searchTerm = filters.search.toLowerCase();
      const searchMatch =
        !filters.search ||
        `${agent.first_name} ${agent.last_name}`
          .toLowerCase()
          .includes(searchTerm) ||
        agent.email?.toLowerCase().includes(searchTerm) ||
        agent.phone?.toLowerCase().includes(searchTerm) ||
        agent.agent?.toLowerCase().includes(searchTerm) ||
        agent.company?.toLowerCase().includes(searchTerm);

      return roleMatch && searchMatch;
    });

    setFilteredList(filtered);
  };

  // Fetch clients
  const getData = async () => {
    try {
      setLoading(true);
      const requestData = await apiClient.get("/getAllUsers");
      const list = requestData.data ?? [];
      setAgents(list);
      setFilteredList(list);
    } catch (error) {
      console.log("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch constants
  const fetchConstants = async () => {
    try {
      setLoading(true);
      const { forms, grades } = await getConstants();
      // Set values
      setForms(forms);
      setGrades(grades);
    } catch (err) {
      console.log("Unable to fetch constants", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Agents
  useEffect(() => {
    getData();
    fetchConstants();
  }, []);

  // Apply filters whenever filters state changes
  useEffect(() => {
    // Make sure agents is defined before filtering
    if (!Array.isArray(agents)) return;

    filterProducts();
  }, [filters]); // Add filters as a dependency

  if (loading || !agents) {
    return <Spinner />;
  }

  return (
    <div className="sectionWrapper mobileWrapper">
      <div className="flex gap-5">
        <div className="cursor-pointer" onClick={() => setMode("agents")}>
          <h1 className="text-sm font-bold text-green ml-0.5 !w-[106px]">
            Manage Clients
          </h1>
          {mode === "agents" && <div className="highlight !w-[106px]" />}
        </div>

        <div className="cursor-pointer" onClick={() => setMode("create")}>
          <h1 className="text-sm font-bold text-green ml-0.5 !w-[106px]">
            Create Account
          </h1>
          {mode === "create" && <div className="highlight !w-[106px]" />}
        </div>

        {
          mode === "edit" &&
          <div className="cursor-pointer" onClick={() => setMode("create")}>
          <h1 className="text-sm font-bold text-green ml-0.5 !w-[106px]">
            Edit Account
          </h1>
          <div className="highlight !w-[90px]" />
        </div>
        }
      </div>

      {/*  Alert  */}
      {visible && accountToDeleteEmail && (
        <DeleteAlert
          setVisible={setVisible}
          email={accountToDeleteEmail}
          deleteAccount={deleteAccount}
        />
      )}

      {mode === "agents" && (
        <>
          <div className="flex flex-col md:flex-row gap-4 my-4">
            <div className="md:w-1/3">
              <label className="block text-sm font-medium mb-1">
                Client Roles
              </label>
              <Select onValueChange={handleRoleChange} value={filters.role}>
                <SelectTrigger className="w-full !h-[40px]">
                  <SelectValue placeholder="Select Role">
                    {filters.role === "all"
                      ? "All Roles"
                      : formatRole(filters.role)}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {["buyer", "seller"].map((role, i) => (
                    <SelectItem key={i} value={role}>
                      {formatRole(role)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="md:w-2/3">
              <label className="block text-sm font-medium mb-1">Search</label>
              <Input
                type="text"
                placeholder="Search by name, company, email, agent, or phone..."
                value={filters.search}
                onChange={handleSearchChange}
                className="w-full"
              />
            </div>
          </div>

          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                Previous
              </Button>
              <Button
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Next
              </Button>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-sm">
                Page {table.getState().pagination.pageIndex + 1} of{" "}
                {table.getPageCount()}
              </span>
              <Select
                value={table.getState().pagination.pageSize.toString()}
                onValueChange={(value) => table.setPageSize(Number(value))}
              >
                <SelectTrigger className="w-[80px]">
                  <span className="text-sm">
                    {table.getState().pagination.pageSize}
                  </span>
                </SelectTrigger>
                <SelectContent className="!w-[80px]">
                  {[10, 25, 50].map((size) => (
                    <SelectItem key={size} value={size.toString()}>
                      Show {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </>
      )}
      {mode === "create" && (
        <>
          <p className="mt-3 ml-3 text-sm text-muted mobileMargin">
            Create a Buyer or Seller client account. Fill the form to create an
            client account.
          </p>

          <Form {...form}>
            <form
              className="space-y-4 p-3"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <div className="flex justify-between mobileFormWrapper">
                <div className="w-[49%] mobileInputWrapper">
                  <FormField
                    control={form.control}
                    name="first_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name*</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="w-[49%] mobileInputWrapper">
                  <FormField
                    control={form.control}
                    name="last_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name*</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="flex justify-between mobileFormWrapper">
                <div className="w-[49%] mobileInputWrapper">
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Phone{" "}
                          <span className="text-muted">
                            (with country code)*
                          </span>
                        </FormLabel>

                        <FormControl>
                          <Input placeholder="Enter phone" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="w-[49%] mobileInputWrapper">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email*</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter email" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="flex justify-between mobileFormWrapper">
                <div className="w-[49%] mobileInputWrapper">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password*</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter password" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Role */}
                <div className="w-[49%] mobileInputWrapper">
                  <FormField
                    control={form.control}
                    name="role"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Role*</FormLabel>
                        <Select
                          onValueChange={(value) => field.onChange(value)}
                          defaultValue={field.value}
                        >
                          <FormControl className="h-10">
                            <SelectTrigger>
                              <SelectValue placeholder="Select role" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {["Buyer", "Seller", "Buyer & Seller"].map(
                              (role) => (
                                <SelectItem key={role} value={role}>
                                  {role}
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="flex justify-between mobileFormWrapper">
                <div className="w-[49%] mobileInputWrapper">
                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company Name*</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter company name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="w-[49%] mobileInputWrapper">
                  <FormField
                    control={form.control}
                    name="source"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Account Source</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter account source" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="text-right">
                <Button
                  type="submit"
                  className="w-[200px] bg-green mobileInputWrapper mt-2"
                >
                  Submit
                </Button>
              </div>
            </form>
          </Form>
        </>
      )}
       {mode === "edit" && (
        <>
          <p className="mt-3 ml-3 text-sm text-muted mobileMargin">
            Edit a Buyer or Seller client account. Fill the form to edit an
            client account.
          </p>

          <Form {...form}>
            <form
              className="space-y-4 p-3"
              onSubmit={form.handleSubmit(onUpdate)}
            >
              <div className="flex justify-between mobileFormWrapper">
                <div className="w-[49%] mobileInputWrapper">
                  <FormField
                    control={form.control}
                    name="first_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name*</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="w-[49%] mobileInputWrapper">
                  <FormField
                    control={form.control}
                    name="last_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name*</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="flex justify-between mobileFormWrapper">
                <div className="w-[49%] mobileInputWrapper">
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Phone{" "}
                          <span className="text-muted">
                            (with country code)*
                          </span>
                        </FormLabel>

                        <FormControl>
                          <Input placeholder="Enter phone" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="w-[49%] mobileInputWrapper">
                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company Name*</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter company name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="flex justify-between mobileFormWrapper">
                <div className="w-[49%] mobileInputWrapper">
                  <FormField
                    control={form.control}
                    name="grade"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Grade of Material*</FormLabel>
                        <Select
                          onValueChange={(val) => {
                            const current = field.value || [];
                            if (current.includes(val)) {
                              field.onChange(current.filter((v) => v !== val));
                            } else {
                              field.onChange([...current, val]);
                            }
                          }}
                        >
                          <FormControl className="h-10">
                            <SelectTrigger>
                              <SelectValue placeholder="Select grade" />
                            </SelectTrigger>
                          </FormControl>
                        
                          <SelectContent>
                            {grades.map((grade) => (
                              <SelectItem key={grade} value={grade}>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={field.value?.includes(grade)}
                                    readOnly
                                  />
                                  {grade}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="w-[49%] mobileInputWrapper">
                  <FormField
                    control={form.control}
                    name="form"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Form of Material*</FormLabel>
                        <Select
                          onValueChange={(val) => {
                            const current = field.value || [];
                            if (current.includes(val)) {
                              field.onChange(current.filter((v) => v !== val));
                            } else {
                              field.onChange([...current, val]);
                            }
                          }}
                        >
                          <FormControl className="h-10">
                            <SelectTrigger>
                              <SelectValue placeholder="Select form" />
                            </SelectTrigger>
                          </FormControl>
                        
                          <SelectContent>
                            {forms.map((form) => (
                              <SelectItem key={form} value={form}>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={field.value?.includes(form)}
                                    readOnly
                                  />
                                  {form}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                          
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="text-right">
                <Button
                  type="submit"
                  className="w-[200px] bg-green mobileInputWrapper mt-2"
                >
                  Update
                </Button>
              </div>
            </form>
          </Form>
        </>
      )}
    </div>
  );
}

export default Clients;
